/**
 * Thin API client.
 *
 * Every request is same-origin with credentials, so the browser performs the
 * Kerberos/NTLM handshake against IIS transparently - there is no login form
 * and no token to manage.
 */

export class ApiError extends Error {
  constructor(message, status, code, body) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

async function request(path, options = {}) {
  let res;
  try {
    res = await fetch(`/api${path}`, {
      credentials: 'same-origin',
      // Never let the browser satisfy an API call from its HTTP cache. The
      // server also sends no-store, but on a shared workstation a stale
      // /api/session/me is the difference between seeing your own data and
      // seeing whoever used the machine before you.
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
      ...options,
    });
  } catch (networkErr) {
    throw new ApiError(`Cannot reach the FSWeb server (${networkErr.message}).`, 0, 'network');
  }

  if (res.status === 204) return null;

  const isJson = (res.headers.get('content-type') || '').includes('application/json');
  const payload = isJson ? await res.json().catch(() => null) : await res.text();

  if (!res.ok) {
    const message =
      (payload && payload.message) ||
      (typeof payload === 'string' && payload.slice(0, 300)) ||
      `Request failed with status ${res.status}.`;
    throw new ApiError(message, res.status, payload?.error, payload);
  }

  return payload;
}

export const api = {
  get: (path) => request(path),
  post: (path, body) => request(path, { method: 'POST', body: JSON.stringify(body ?? {}) }),
  put: (path, body) => request(path, { method: 'PUT', body: JSON.stringify(body ?? {}) }),
  del: (path) => request(path, { method: 'DELETE' }),

  /** Execute a catalogued stored procedure and return its rows. */
  async proc(name, params = {}) {
    const r = await request(`/proc/${name}`, { method: 'POST', body: JSON.stringify(params) });
    return r.rows || [];
  },

  /** Execute a proc that returns several result sets. */
  async procSets(name, params = {}) {
    const r = await request(`/proc/${name}`, { method: 'POST', body: JSON.stringify(params) });
    return r.recordsets || [r.rows || []];
  },

  /** Download the current result set as .xlsx. */
  async exportXlsx(procName, { params = {}, columns = [], filename, sheetName, groupBy, title } = {}) {
    const res = await fetch(`/api/export/${procName}`, {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ params, columns, filename, sheetName, groupBy, title }),
    });
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new ApiError(`Export failed: ${t.slice(0, 200) || res.status}`, res.status, 'export');
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename || procName}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  },

};

export default api;
